import { useState, useEffect } from 'react';
import { plural } from '../lib/utils';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { getStats, getProjects, getMasters, getMovements, MASTER_STATUSES, MOVEMENT_TYPES, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { 
    HardDrive, 
    Film, 
    TrendingUp, 
    Package, 
    AlertTriangle,
    Truck,
    Plus,
    ArrowRight,
    Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [recentProjects, setRecentProjects] = useState([]);
    const [recentMovements, setRecentMovements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, projectsRes, movementsRes] = await Promise.all([
                getStats(),
                getProjects({ archived: false }),
                getMovements()
            ]);
            setStats(statsRes.data);
            setRecentProjects(projectsRes.data.slice(0, 5));
            setRecentMovements(movementsRes.data.slice(0, 5));
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement des données'));
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
        <Card className="bg-card border-border hover:border-primary/30 transition-colors">
            <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">
                            {title}
                        </p>
                        <p className="text-3xl font-heading font-bold mt-2">{value}</p>
                        {subtitle && (
                            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                        )}
                    </div>
                    <div className={`hidden sm:block p-3 rounded ${color}`} aria-hidden="true">
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-8 animate-fade-in" data-testid="dashboard">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            TABLEAU DE BORD
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Vue d'ensemble de votre stock DCP
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button asChild variant="outline" data-testid="new-project-btn">
                            <Link to="/projects/new">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouveau projet
                            </Link>
                        </Button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <StatCard
                        title="Total Masters"
                        value={stats?.total_masters || 0}
                        icon={HardDrive}
                        color="bg-primary/20 text-primary"
                    />
                    <StatCard
                        title="En Stock"
                        value={stats?.in_stock || 0}
                        icon={Package}
                        color="bg-tone-stock-base/20 text-tone-stock"
                        subtitle={`${stats?.at_client || 0} chez le client`}
                    />
                    <StatCard
                        title="Projets"
                        value={stats?.total_projects || 0}
                        icon={Film}
                        color="bg-tone-client-base/20 text-tone-client"
                    />
                    <StatCard
                        title="Mouvements (7j)"
                        value={stats?.recent_movements || 0}
                        icon={TrendingUp}
                        color="bg-tone-ecrase-base/20 text-tone-ecrase"
                    />
                </div>

                {/* Alert for lost/out of service */}
                {(stats?.lost > 0 || stats?.out_of_service > 0) && (
                    <Card className="border-tone-alerte-base/30 bg-tone-alerte-base/5">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="w-5 h-5 text-tone-alerte" />
                                <div>
                                    <p className="font-medium text-tone-alerte">Attention</p>
                                    <p className="text-sm text-muted-foreground">
                                        {stats?.lost > 0 && `${plural(stats.lost, 'master marqué perdu', 'masters marqués perdus')}`}
                                        {stats?.lost > 0 && stats?.out_of_service > 0 && ' • '}
                                        {stats?.out_of_service > 0 && `${plural(stats.out_of_service, 'master hors service', 'masters hors service')}`}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Projects */}
                    <Card className="bg-card border-border">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="font-heading text-lg font-semibold">
                                Projets récents
                            </CardTitle>
                            <Button variant="ghost" size="sm" asChild>
                                <Link to="/projects" className="text-primary">
                                    Voir tout
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </Link>
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {recentProjects.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Film className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                    <p>Aucun projet</p>
                                    <Button asChild variant="link" className="mt-2">
                                        <Link to="/projects/new">Créer un projet</Link>
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {recentProjects.map((project) => (
                                        <Link
                                            key={project.id}
                                            to={`/projects/${project.id}`}
                                            className="flex items-center gap-4 p-3 rounded-sm bg-secondary/30 hover:bg-secondary/50 transition-colors group"
                                            data-testid={`project-item-${project.id}`}
                                        >
                                            {project.poster_thumb ? (
                                                <img
                                                    src={project.poster_thumb}
                                                    alt={project.title}
                                                    className="w-12 h-16 object-cover rounded"
                                                />
                                            ) : (
                                                <div className="w-12 h-16 bg-muted rounded flex items-center justify-center">
                                                    <Film className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate group-hover:text-primary transition-colors">
                                                    {project.title}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {project.production_year || 'N/A'} • {plural(project.masters_count, 'master')}
                                                </p>
                                            </div>
                                            {project.favorite && (
                                                <Badge variant="outline" className="border-primary/50 text-primary">
                                                    Favori
                                                </Badge>
                                            )}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Movements */}
                    <Card className="bg-card border-border">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="font-heading text-lg font-semibold">
                                Derniers mouvements
                            </CardTitle>
                            <Button variant="ghost" size="sm" asChild>
                                <Link to="/movements" className="text-primary">
                                    Voir tout
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </Link>
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {recentMovements.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Truck className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                    <p>Aucun mouvement</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {recentMovements.map((movement) => (
                                        <div
                                            key={movement.id}
                                            className="flex items-center gap-4 p-3 rounded-sm bg-secondary/30"
                                            data-testid={`movement-item-${movement.id}`}
                                        >
                                            <div className={`p-2 rounded ${
                                                movement.type === 'reception' 
                                                    ? 'bg-tone-stock-base/20 text-tone-stock'
                                                    : movement.type === 'expedition'
                                                    ? 'bg-tone-client-base/20 text-tone-client'
                                                    : 'bg-tone-ecrase-base/20 text-tone-ecrase'
                                            }`}>
                                                <Truck className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">
                                                    {MOVEMENT_TYPES[movement.type] || movement.type}
                                                </p>
                                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(movement.date), 'dd MMM yyyy', { locale: fr })}
                                                </p>
                                            </div>
                                            {movement.tracking_number && (
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {movement.tracking_number}
                                                </Badge>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Stock Status Overview */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg font-semibold">
                            Répartition du stock
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                            {[
                                { key: 'in_stock', label: 'En stock', value: stats?.in_stock || 0, color: 'bg-tone-stock-base' },
                                { key: 'at_client', label: 'Chez client', value: stats?.at_client || 0, color: 'bg-tone-client-base' },
                                { key: 'out_of_stock', label: 'Hors stock', value: stats?.out_of_stock || 0, color: 'bg-tone-neutre-base' },
                                { key: 'lost', label: 'Perdu', value: stats?.lost || 0, color: 'bg-tone-perdu-base' },
                                { key: 'out_of_service', label: 'Hors service', value: stats?.out_of_service || 0, color: 'bg-tone-alerte-base' },
                                { key: 'overwritten', label: 'Écrasé', value: stats?.overwritten || 0, color: 'bg-tone-ecrase-base' },
                            ].map((item) => (
                                <div key={item.key} className="text-center p-4 rounded-sm bg-secondary/30">
                                    <div className={`w-3 h-3 rounded-full ${item.color} mx-auto mb-2`} />
                                    <p className="text-2xl font-heading font-bold">{item.value}</p>
                                    <p className="text-xs text-muted-foreground">{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
